/**
 * Rounds Service
 * 
 * Handles all userRounds collection operations with the exact schema
 * as specified in the project requirements.
 */

import { serverTimestamp } from 'firebase/firestore';
import {
  setDocument,
  getDocument,
  updateDocument,
  getDocuments,
  type FirestoreQueryOptions
} from '@/firebase/firestore';
import {
  FirebaseUserRound,
  CreateRoundData,
  FIREBASE_COLLECTIONS,
  type FirebaseServiceResponse,
  type RoundsListener
} from '@/types/firebase';
import { SessionStats, FingerType } from '@/types';

/**
 * EXACT SCHEMA IMPLEMENTATION
 * Based on specification: userRounds/{roundId}
 */

// Finger mapping for specification compliance
const FINGER_DISPLAY_NAMES: Record<FingerType, string> = {
  'leftPinky': 'pinky',
  'leftRing': 'ring', 
  'leftMiddle': 'middle',
  'leftPointing': 'pointing',
  'leftThumb': 'big',
  'rightPinky': 'pinky',
  'rightRing': 'ring',
  'rightMiddle': 'middle', 
  'rightPointing': 'pointing',
  'rightThumb': 'big'
};

// Complexity mapping for specification compliance
const COMPLEXITY_DISPLAY_NAMES: Record<string, string> = {
  '1': '1',
  '3': '1_3', 
  '5': '1_5',
  'words': 'w'
};

/**
 * Create a new training round in Firestore
 */
export const createRound = async (
  uid: string,
  roundData: CreateRoundData
): Promise<FirebaseServiceResponse<FirebaseUserRound>> => {
  try {
    console.log('📝 Creating new training round for user:', uid);
    
    // Generate unique round ID
    const roundId = `round_${uid}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Map fingers to specification format
    const fingersDisplay = Array.from(new Set(
      roundData.fingers.map(finger => FINGER_DISPLAY_NAMES[finger])
    ));
    
    // Map complexity to specification format
    const complexityDisplay = COMPLEXITY_DISPLAY_NAMES[roundData.complexity.toString()] || roundData.complexity.toString();
    
    // Create round document with EXACT schema
    const newRound: FirebaseUserRound = {
      roundId,
      uid,
      
      // Training context - only set defTraining fields for defTraining type
      trainingType: roundData.trainingType,
      ...(roundData.trainingType === 'defTraining' && {
        defTrainingDay: roundData.defTrainingDay,
        defTrainingRound: roundData.defTrainingRound
      }),
      
      // Session metadata
      timestamp: serverTimestamp() as any,
      duration: roundData.duration,
      
      // Training configuration
      fingers: fingersDisplay,
      hands: roundData.hands,
      complexity: complexityDisplay as '1' | '1_3' | '1_5' | 'w',
      
      // Per-letter statistics (ensure all fields present)
      letters: roundData.letters || {},
      
      // Word-based statistics (ensure all fields present)
      words: {
        wordsTotal: roundData.words?.wordsTotal || 0,
        wordsCorrect: roundData.words?.wordsCorrect || 0,
        wordsWrong: roundData.words?.wordsWrong || 0,
        word1Fastest: roundData.words?.word1Fastest || 0,
        word2Fastest: roundData.words?.word2Fastest || 0,
        word3Fastest: roundData.words?.word3Fastest || 0,
        word4Fastest: roundData.words?.word4Fastest || 0,
        word5Fastest: roundData.words?.word5Fastest || 0,
        word6Fastest: roundData.words?.word6Fastest || 0,
        word7Fastest: roundData.words?.word7Fastest || 0
      },
      
      // Overall round statistics
      overallStats: roundData.overallStats
    };
    
    console.log('📤 Creating Firebase document:', {
      roundId,
      collection: FIREBASE_COLLECTIONS.USER_ROUNDS,
      lettersCount: Object.keys(newRound.letters).length,
      wordsStats: newRound.words,
      overallStats: newRound.overallStats
    });
    
    const result = await setDocument(
      FIREBASE_COLLECTIONS.USER_ROUNDS,
      roundId,
      newRound
    );
    
    if (result.success) {
      console.log('✅ Round created successfully:', roundId);
      console.log('📈 Document data verification:', {
        hasLetters: Object.keys(newRound.letters).length > 0,
        lettersPreview: Object.keys(newRound.letters).slice(0, 3),
        wordsTotal: newRound.words.wordsTotal
      });
      return {
        success: true,
        data: result.data as FirebaseUserRound
      };
    } else {
      console.error('🚫 Failed to create round:', result.error);
      return {
        success: false,
        error: {
          code: 'round/creation-failed',
          message: result.error || 'Failed to create round'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error creating round:', error);
    return {
      success: false,
      error: {
        code: 'round/creation-error',
        message: error instanceof Error ? error.message : 'Unknown error creating round'
      }
    };
  }
};

/**
 * Update round statistics during or after session
 */
export const updateRoundStats = async (
  roundId: string,
  updates: {
    letters?: FirebaseUserRound['letters'];
    words?: Partial<FirebaseUserRound['words']>;
    overallStats?: SessionStats;
    duration?: number;
  }
): Promise<FirebaseServiceResponse<FirebaseUserRound>> => {
  try {
    console.log('📊 Updating round statistics:', roundId);
    
    // Prepare update data with proper field structure
    const updateData: any = {};
    
    if (updates.letters) {
      updateData.letters = updates.letters;
    }
    
    if (updates.words) {
      // Ensure all word fields are present when updating
      updateData.words = {
        wordsTotal: updates.words.wordsTotal || 0,
        wordsCorrect: updates.words.wordsCorrect || 0,
        wordsWrong: updates.words.wordsWrong || 0,
        word1Fastest: updates.words.word1Fastest || 0,
        word2Fastest: updates.words.word2Fastest || 0,
        word3Fastest: updates.words.word3Fastest || 0,
        word4Fastest: updates.words.word4Fastest || 0,
        word5Fastest: updates.words.word5Fastest || 0,
        word6Fastest: updates.words.word6Fastest || 0,
        word7Fastest: updates.words.word7Fastest || 0
      };
    }
    
    if (updates.overallStats) {
      updateData.overallStats = updates.overallStats;
    }
    
    if (updates.duration !== undefined) {
      updateData.duration = updates.duration;
    }
    
    console.log('📤 Updating Firebase round stats:', {
      roundId,
      updateFields: Object.keys(updateData),
      lettersCount: updates.letters ? Object.keys(updates.letters).length : 0,
      wordsTotal: updates.words?.wordsTotal || 0,
      accuracy: updates.overallStats?.accuracy || 0
    });
    
    const result = await updateDocument(
      FIREBASE_COLLECTIONS.USER_ROUNDS,
      roundId,
      updateData
    );
    
    if (result.success) {
      console.log('✅ Round statistics updated successfully:', roundId);
      console.log('📈 Update verification:', {
        updatedFields: Object.keys(updateData),
        lettersUpdated: updateData.letters ? Object.keys(updateData.letters).length : 0,
        wordsUpdated: updateData.words?.wordsTotal || 0
      });
      return {
        success: true,
        data: result.data as FirebaseUserRound
      };
    } else {
      console.error('🚫 Failed to update round statistics:', result.error);
      return {
        success: false,
        error: {
          code: 'round/update-failed',
          message: result.error || 'Failed to update round statistics'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error updating round statistics:', error);
    return {
      success: false,
      error: {
        code: 'round/update-error',
        message: error instanceof Error ? error.message : 'Unknown error updating round'
      }
    };
  }
};

/**
 * Get a specific round by ID
 */
export const getRound = async (roundId: string): Promise<FirebaseServiceResponse<FirebaseUserRound>> => {
  try {
    console.log('📖 Getting round:', roundId);
    
    const result = await getDocument<FirebaseUserRound>(
      FIREBASE_COLLECTIONS.USER_ROUNDS,
      roundId
    );
    
    if (result.success && result.data) {
      console.log('✅ Round retrieved successfully:', roundId);
      return {
        success: true,
        data: result.data
      };
    } else {
      console.log('📭 Round not found:', roundId);
      return {
        success: false,
        error: {
          code: 'round/not-found',
          message: 'Round not found'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error getting round:', error);
    return {
      success: false,
      error: {
        code: 'round/fetch-error',
        message: error instanceof Error ? error.message : 'Unknown error fetching round'
      }
    };
  }
};

/**
 * Get user's rounds with filtering options
 */
export const getUserRounds = async (
  uid: string,
  options: {
    trainingType?: 'defTraining' | 'customTraining';
    defTrainingDay?: number;
    limit?: number;
    orderBy?: 'timestamp' | 'defTrainingDay';
    orderDirection?: 'asc' | 'desc';
  } = {}
): Promise<FirebaseServiceResponse<FirebaseUserRound[]>> => {
  try {
    console.log('📋 Getting user rounds:', uid, options);
    
    const queryOptions: FirestoreQueryOptions = {
      whereConditions: [
        { field: 'uid', operator: '==', value: uid }
      ]
    };
    
    // Add training type filter
    if (options.trainingType) {
      queryOptions.whereConditions!.push({
        field: 'trainingType',
        operator: '==',
        value: options.trainingType
      });
    }
    
    // Add training day filter
    if (options.defTrainingDay !== undefined) {
      queryOptions.whereConditions!.push({
        field: 'defTrainingDay',
        operator: '==',
        value: options.defTrainingDay
      });
    }
    
    // Add ordering
    if (options.orderBy) {
      queryOptions.orderByField = options.orderBy;
      queryOptions.orderDirection = options.orderDirection || 'desc';
    }
    
    // Add limit
    if (options.limit) {
      queryOptions.limitCount = options.limit;
    }
    
    const result = await getDocuments<FirebaseUserRound>(
      FIREBASE_COLLECTIONS.USER_ROUNDS,
      queryOptions
    );
    
    if (result.success) {
      console.log(`✅ Retrieved ${result.data?.length || 0} rounds for user:`, uid);
      return {
        success: true,
        data: result.data || []
      };
    } else {
      console.error('🚫 Failed to get user rounds:', result.error);
      return {
        success: false,
        error: {
          code: 'round/query-failed',
          message: result.error || 'Failed to query user rounds'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error getting user rounds:', error);
    return {
      success: false,
      error: {
        code: 'round/query-error',
        message: error instanceof Error ? error.message : 'Unknown error querying rounds'
      }
    };
  }
};

/**
 * Get rounds for a specific training day
 */
export const getTrainingDayRounds = async (
  uid: string,
  trainingDay: number
): Promise<FirebaseServiceResponse<FirebaseUserRound[]>> => {
  return getUserRounds(uid, {
    trainingType: 'defTraining',
    defTrainingDay: trainingDay,
    orderBy: 'defTrainingDay',
    orderDirection: 'asc'
  });
};

/**
 * Listen to real-time updates for user's rounds
 */
export const listenToUserRounds = (
  uid: string,
  callback: RoundsListener,
  options: {
    trainingType?: 'defTraining' | 'customTraining';
    defTrainingDay?: number;
    limit?: number;
  } = {}
): (() => void) => {
  console.log('👂 Setting up rounds listener for user:', uid);
  
  // This would use listenToCollection but we need to implement it properly
  // For now, we'll use periodic polling as a fallback
  const pollInterval = setInterval(async () => {
    const result = await getUserRounds(uid, options);
    if (result.success) {
      callback(result.data || [], undefined);
    } else {
      callback([], result.error);
    }
  }, 5000); // Poll every 5 seconds
  
  // Return cleanup function
  return () => {
    clearInterval(pollInterval);
  };
};

/**
 * Helper function to initialize letter statistics for a round
 */
export const initializeLetterStats = (letters: string[]): FirebaseUserRound['letters'] => {
  const letterStats: FirebaseUserRound['letters'] = {};
  
  // Get unique letters
  const uniqueLetters = Array.from(new Set(letters.map(l => l.toUpperCase())));
  
  uniqueLetters.forEach(letter => {
    letterStats[letter] = {
      tapTotal: 0,
      tapCorrect: 0,
      tapWrong: 0,
      tapFastest: 0
    };
  });
  
  return letterStats;
};

/**
 * Helper function to initialize word statistics
 */
export const initializeWordStats = (): FirebaseUserRound['words'] => {
  return {
    wordsTotal: 0,
    wordsCorrect: 0,
    wordsWrong: 0,
    word1Fastest: 0,
    word2Fastest: 0,
    word3Fastest: 0,
    word4Fastest: 0,
    word5Fastest: 0,
    word6Fastest: 0,
    word7Fastest: 0
  };
};

/**
 * Utility to update letter statistics during gameplay
 */
export const updateLetterStat = (
  letterStats: FirebaseUserRound['letters'],
  letter: string,
  isCorrect: boolean,
  reactionTime: number
): void => {
  const upperLetter = letter.toUpperCase();
  
  if (!letterStats[upperLetter]) {
    letterStats[upperLetter] = {
      tapTotal: 0,
      tapCorrect: 0,
      tapWrong: 0,
      tapFastest: 0
    };
  }
  
  const stats = letterStats[upperLetter];
  stats.tapTotal++;
  
  if (isCorrect) {
    stats.tapCorrect++;
    
    // Update fastest time (only for correct taps)
    if (stats.tapFastest === 0 || reactionTime < stats.tapFastest) {
      stats.tapFastest = reactionTime;
    }
  } else {
    stats.tapWrong++;
  }
};

/**
 * Utility to update word statistics
 */
export const updateWordStat = (
  wordStats: FirebaseUserRound['words'],
  wordLength: number,
  isCorrect: boolean,
  completionTime: number
): void => {
  wordStats.wordsTotal++;
  
  if (isCorrect) {
    wordStats.wordsCorrect++;
    
    // Update fastest time for word length (only for correct words)
    const fastestField = `word${Math.min(wordLength, 7)}Fastest` as keyof FirebaseUserRound['words'];
    if (typeof wordStats[fastestField] === 'number') {
      const currentFastest = wordStats[fastestField] as number;
      if (currentFastest === 0 || completionTime < currentFastest) {
        (wordStats[fastestField] as number) = completionTime;
      }
    }
  } else {
    wordStats.wordsWrong++;
  }
};

// Development helpers
if (import.meta.env.DEV) {
  console.log('📝 Rounds service initialized in development mode');
}
